import React, { useMemo, useState } from 'react';
import { Select } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import TransactionPublisherTable from './TransactionPublisherTable';
import TransactionPublisherRingChart from './TransactionPublisherRingChart';
import EntityBreakdownCard from '../../../../../../../shared/designSystem/components/EntityBreakdownCard';
import { MenuItemText } from '../../../../../../../shared/globalStyling/styledText';
import { getRingColor } from '../../../../../../../shared/designSystem/components/BreakdownRingChart';

const { Option } = Select;

const SHOW_BY_OPTIONS = [
  { value: 'impressions', text: 'Impressions' },
  { value: 'totalTransactions', text: 'Transactions' },
];

// =============================================================================
// TransactionPublisherDetails — unified Publishers + Breakdown card (V2 tab).
//
// Preserves v1's same-name consolidation logic: publishers sharing a `name`
// get merged into one row (sum impressions + transactions, first media
// wins). Then sorts desc by impressions and assigns stable slice colors.
// Structural parity with the Creatives card in this tab.
// =============================================================================

const TransactionPublisherDetails = ({
  publishers = [],
  loading = false,
  byDateLoading = false,
  showMetrics = false,
}) => {
  const [publisherDimension, setPublisherDimension] = useState('impressions');

  const publishersWithColors = useMemo(() => {
    // Consolidate publishers with the same name. v1 logic, preserved.
    const consolidated = publishers.reduce((acc, publisher) => {
      const name = publisher.name || publisher.media || 'Unknown';
      const existing = acc.find((p) => p.name === name);
      if (existing) {
        existing.impressions += publisher.impressions || 0;
        existing.transactions += publisher.transactions || 0;
        if (!existing.media && publisher.media) {
          existing.media = publisher.media;
        }
      } else {
        acc.push({
          ...publisher,
          name,
          impressions: publisher.impressions || 0,
          transactions: publisher.transactions || 0,
        });
      }
      return acc;
    }, []);

    return consolidated
      .sort((a, b) => b.impressions - a.impressions)
      .map((publisher, index) => ({
        ...publisher,
        color: getRingColor(index),
        value: publisher.name || publisher.media || `publisher-${index}`,
        totalTransactions: publisher.transactions,
      }));
  }, [publishers]);

  return (
    <EntityBreakdownCard
      title={
        <>
          Publishers
          {byDateLoading && (
            <LoadingOutlined
              style={{ marginLeft: 8, fontSize: 14, color: '#94a3b8' }}
              spin
              aria-label="Loading by-date data"
            />
          )}
        </>
      }
      loading={loading}
      table={
        <TransactionPublisherTable
          publisherData={publishersWithColors}
          showMetrics={showMetrics}
        />
      }
      breakdownControl={
        <Select
          value={publisherDimension}
          onChange={(value) => setPublisherDimension(value)}
          getPopupContainer={(trigger) => trigger.parentNode}
          popupClassName="mjl-ds"
          className="campaign-overview__creatives-showby"
          size="small"
        >
          {SHOW_BY_OPTIONS.map((option) => (
            <Option key={option.value} value={option.value}>
              <MenuItemText text={option.text} />
            </Option>
          ))}
        </Select>
      }
      breakdownChart={
        <TransactionPublisherRingChart
          data={publishersWithColors}
          height={480}
          selectedPublisherDimension={publisherDimension}
        />
      }
    />
  );
};

export default TransactionPublisherDetails;
